import { FC } from "react";
import { Button, TextField } from "@mui/material";
import ValidationError from "@components/ValidationError/ValidationError";
import { useTranslation } from "react-i18next";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import { AuthService } from "@service/auth.service";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setEmail } from "@store/resetPasswordSlice";
import PasswordField, {
  PasswordFieldFormType,
} from "@components/PasswordField/PasswordField";
import { setMessage } from "@store/snackbarSlice";
import CustomLabelInput from "@components/CustomLabelInput";
import PublicForm from "@components/PublicForm";

const authService = AuthService.instance;

interface PasswordFormType {
  password: string;
  code: string;
}

const PasswordForm: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const email = useSelector((state: RootState) => state.resetPassword.email);
  const dispatch = useDispatch();

  const form = useForm<PasswordFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting, errors },
  } = form;

  const resetPassword = async ({
    password,
    code,
  }: FieldValues): Promise<void> => {
    if (!email || !password || !code) {
      return;
    }
    try {
      await authService.confirmResetPassword(email, code, password);
      dispatch(setEmail(null));
      navigate("/sign-in");
    } catch (e) {
      const type =
        (e as any).name === "CodeMismatchException"
          ? "verificationCodeMismatch"
          : "unknown";

      dispatch(setMessage({ content: t(`errors.${type}`), severity: "error" }));
    }
  };

  return (
    <PublicForm.PublicFormContainer
      onSubmit={(e) => {
        clearErrors();
        return handleSubmit(resetPassword)(e);
      }}
    >
      <CustomLabelInput label={t("resetPassword.codeInput.label")} id="code">
        <TextField
          {...register("code", {
            required: true,
          })}
          id="code"
          error={!!errors?.code}
          type="code"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("resetPassword.codeInput.placeholder")}
          label={t("resetPassword.codeInput.label")}
        />
      </CustomLabelInput>

      <PasswordField
        form={form as unknown as UseFormReturn<PasswordFieldFormType>}
      />

      {errors.root && (
        <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
      )}

      <PublicForm.PublicFormActionContainer>
        <Button
          type="submit"
          disabled={isSubmitting}
          variant="contained"
          size="large"
          fullWidth
        >
          {t("resetPassword.resetButton")}
        </Button>
      </PublicForm.PublicFormActionContainer>
    </PublicForm.PublicFormContainer>
  );
};

export default PasswordForm;
